/**
 * AI Document Analyzer - Advanced Edition
 * Professional construction document analysis with AI-powered insights
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Upload, FileText, Brain, Sparkles, Trash2, Eye, RefreshCw, 
  Search, Filter, Download, MessageSquare, Send, Bot, Copy,
  CheckCircle, AlertCircle, Clock, FileImage, FileSpreadsheet,
  ChevronRight, X, Loader2, Zap, BarChart3, Shield, Calendar,
  Users, DollarSign, Package, Wrench, AlertTriangle, FileCheck
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// API Base URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type configuration
const FILE_ICONS = {
  pdf: FileText,
  image: FileImage,
  excel: FileSpreadsheet,
  word: FileText,
  text: FileText,
  default: FileText
};

const FILE_COLORS = {
  pdf: 'text-red-500',
  image: 'text-blue-500',
  excel: 'text-green-500',
  word: 'text-blue-600',
  text: 'text-gray-500'
};

// Status badge configuration
const STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'Pending' },
  analyzing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Loader2, label: 'Analyzing' },
  completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Analyzed' },
  failed: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle, label: 'Failed' }
};

const DocumentAnalyzer = () => {
  const { getSessionToken, user } = useAuth();
  
  // Core state
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, analyzed: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('library');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState('summary');
  
  // Operation state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyzing, setAnalyzing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // Quick analysis state
  const [quickFile, setQuickFile] = useState(null);
  const [quickQuestion, setQuickQuestion] = useState('');
  const [quickAnalyzing, setQuickAnalyzing] = useState(false);
  const [quickResult, setQuickResult] = useState(null);

  // Get auth token - try multiple sources with logging
  const getToken = useCallback(() => {
    // Try getSessionToken from context first
    if (getSessionToken) {
      const token = getSessionToken();
      if (token) {
        console.log('[AUTH] Token from context:', token.substring(0, 20) + '...');
        return token;
      }
    }
    
    // Fallback to localStorage
    const localToken = localStorage.getItem('session_token');
    if (localToken) {
      console.log('[AUTH] Token from localStorage:', localToken.substring(0, 20) + '...');
      return localToken;
    }
    
    // Try cookies as last resort
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token' && value) {
        console.log('[AUTH] Token from cookie:', value.substring(0, 20) + '...');
        return value;
      }
    }
    
    console.log('[AUTH] No token found!');
    return null;
  }, [getSessionToken]);

  // API call helper with authentication
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    const config = {
      ...options,
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    };
    
    // Don't set Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(options.body);
    }
    
    const response = await fetch(`${API_URL}${endpoint}`, config);
    return response;
  }, [getToken]);

  // Load documents
  const loadDocuments = useCallback(async () => {
    try {
      const response = await apiCall('/api/documents/list');
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await apiCall('/api/documents/stats/summary');
      if (response.ok) {
        const data = await response.json();
        setStats({
          total: data.total_documents || 0,
          analyzed: data.analyzed || 0,
          pending: data.pending || 0,
          failed: data.failed || 0
        });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [apiCall]);

  // Initial load
  useEffect(() => {
    loadDocuments();
    loadStats();
  }, [loadDocuments, loadStats]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Upload documents
  const handleUpload = async (files) => {
    if (!files?.length) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    let successCount = 0;
    const totalFiles = files.length;
    
    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        setUploadProgress(((i + 0.5) / totalFiles) * 100);
        
        const response = await apiCall('/api/documents/upload', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          successCount++;
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.error(`Error uploading ${file.name}`);
      }
      
      setUploadProgress(((i + 1) / totalFiles) * 100);
    }
    
    setUploading(false);
    setUploadProgress(0);
    
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded`);
      loadDocuments();
      loadStats();
    }
  };

  // Analyze document
  const handleAnalyze = async (docId) => {
    setAnalyzing(docId);
    
    try {
      const response = await apiCall(`/api/documents/analyze/${docId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Analysis complete!');
        
        // Update document in state
        setDocuments(prev => prev.map(doc => 
          doc.document_id === docId 
            ? { ...doc, analysis_status: 'completed', analysis_result: data.analysis }
            : doc
        ));
        
        loadStats();
        
        // Open dialog with results
        const updatedDoc = documents.find(d => d.document_id === docId);
        if (updatedDoc) {
          setSelectedDoc({ ...updatedDoc, analysis_status: 'completed', analysis_result: data.analysis });
          setShowDialog(true);
          setDialogTab('summary');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Analysis failed');
        
        setDocuments(prev => prev.map(doc => 
          doc.document_id === docId 
            ? { ...doc, analysis_status: 'failed' }
            : doc
        ));
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(null);
    }
  };

  // View document analysis
  const handleView = async (docId) => {
    try {
      const response = await apiCall(`/api/documents/${docId}`);
      
      if (response.ok) {
        const data = await response.json();
        setSelectedDoc(data.document);
        setChatMessages([]);
        setShowDialog(true);
        setDialogTab('summary');
      }
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  // DELETE DOCUMENT - Simplified direct delete
  const handleDelete = async (docId) => {
    if (!docId) {
      toast.error('No document selected');
      return;
    }
    
    // Set deleting state immediately
    setDeleting(docId);
    
    // Remove from UI FIRST (optimistic update)
    const previousDocs = [...documents];
    setDocuments(docs => docs.filter(d => d.document_id !== docId));
    setStats(s => ({ ...s, total: Math.max(0, s.total - 1) }));
    
    // Close dialog if open
    if (selectedDoc?.document_id === docId) {
      setShowDialog(false);
      setSelectedDoc(null);
    }
    
    try {
      // Get token
      const token = localStorage.getItem('session_token');
      
      if (!token) {
        // Revert UI
        setDocuments(previousDocs);
        toast.error('Please log in first');
        setDeleting(null);
        return;
      }
      
      // Make delete request
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Deleted!');
        // Refresh to confirm
        loadDocuments();
        loadStats();
      } else if (res.status === 404) {
        // Document already deleted - just refresh the list
        toast.success('Document removed');
        loadDocuments();
        loadStats();
      } else {
        // Revert UI on failure
        setDocuments(previousDocs);
        toast.error('Delete failed');
      }
    } catch (err) {
      // Revert UI on error
      setDocuments(previousDocs);
      toast.error('Delete error');
    } finally {
      setDeleting(null);
    }
  };

  // Chat with document
  const handleChat = async () => {
    if (!chatInput.trim() || !selectedDoc?.document_id) return;
    
    const question = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: question }]);
    setChatLoading(true);
    
    try {
      const response = await apiCall(`/api/documents/${selectedDoc.document_id}/chat`, {
        method: 'POST',
        body: { question }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
      } else {
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I could not process your question. Please try again.' 
        }]);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Connection error. Please try again.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Export chat
  const handleExportChat = async () => {
    if (!selectedDoc?.document_id || chatMessages.length === 0) return;
    
    try {
      const response = await apiCall(`/api/documents/${selectedDoc.document_id}/chat/export`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.export, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat_${selectedDoc.filename}_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Chat exported!');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Clear chat
  const handleClearChat = async () => {
    if (!selectedDoc?.document_id) return;
    
    try {
      await apiCall(`/api/documents/${selectedDoc.document_id}/chat/history`, {
        method: 'DELETE'
      });
      setChatMessages([]);
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  // Quick analysis
  const handleQuickAnalysis = async () => {
    if (!quickFile) return;
    
    setQuickAnalyzing(true);
    setQuickResult(null);
    
    const formData = new FormData();
    formData.append('file', quickFile);
    formData.append('question', quickQuestion || 'Provide a comprehensive analysis');
    
    try {
      const response = await apiCall('/api/documents/quick-analyze', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuickResult(data);
        toast.success('Analysis complete!');
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setQuickAnalyzing(false);
    }
  };

  // Filter documents
  const filteredDocs = documents.filter(doc => {
    const matchesSearch = doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || doc.file_type === filterType;
    const matchesStatus = filterStatus === 'all' || doc.analysis_status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get file icon
  const getFileIcon = (type) => {
    const Icon = FILE_ICONS[type] || FILE_ICONS.default;
    return Icon;
  };

  // Render stat card
  const StatCard = ({ value, label, icon: Icon, color }) => (
    <div className={`p-4 rounded-xl ${color} transition-all hover:scale-105`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-sm opacity-80">{label}</p>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </div>
  );

  // Render document card
  const DocumentCard = ({ doc }) => {
    const FileIcon = getFileIcon(doc.file_type);
    const statusConfig = STATUS_CONFIG[doc.analysis_status] || STATUS_CONFIG.pending;
    const StatusIcon = statusConfig.icon;
    const isDeleting = deleting === doc.document_id;
    const isAnalyzing = analyzing === doc.document_id;
    
    return (
      <Card className={`group transition-all hover:shadow-lg border-2 ${isDeleting ? 'opacity-50 scale-95' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {/* File Icon */}
            <div className={`p-3 rounded-xl bg-gray-100 ${FILE_COLORS[doc.file_type] || 'text-gray-500'}`}>
              <FileIcon className="w-6 h-6" />
            </div>
            
            {/* Document Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate" title={doc.filename}>
                {doc.filename}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                <Badge variant="outline" className="text-xs">
                  {doc.file_type?.toUpperCase()}
                </Badge>
                <span>{formatSize(doc.file_size)}</span>
                <span>•</span>
                <span>{formatDate(doc.created_at)}</span>
              </div>
              <div className="mt-2">
                <Badge className={`${statusConfig.color} border`}>
                  <StatusIcon className={`w-3 h-3 mr-1 ${doc.analysis_status === 'analyzing' ? 'animate-spin' : ''}`} />
                  {statusConfig.label}
                </Badge>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {doc.analysis_status === 'completed' ? (
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => handleView(doc.document_id)}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                >
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
              ) : doc.analysis_status === 'pending' || doc.analysis_status === 'failed' ? (
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => handleAnalyze(doc.document_id)}
                  disabled={isAnalyzing}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                >
                  {isAnalyzing ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Brain className="w-4 h-4 mr-1" /> Analyze</>
                  )}
                </Button>
              ) : null}
              
              {/* Delete Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(doc.document_id)}
                disabled={isDeleting}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-btn-${doc.document_id}`}
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render analysis section
  const renderAnalysis = (analysis) => {
    if (!analysis) return null;
    
    return (
      <div className="space-y-6">
        {/* Executive Summary */}
        {analysis.executive_summary && (
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-blue-500" />
                Executive Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {analysis.executive_summary}
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-blue-50 text-center">
            <Package className="w-6 h-6 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-blue-700">{analysis.materials?.length || 0}</p>
            <p className="text-xs text-blue-600">Materials</p>
          </div>
          <div className="p-4 rounded-lg bg-green-50 text-center">
            <Users className="w-6 h-6 mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold text-green-700">{analysis.labor_items?.length || 0}</p>
            <p className="text-xs text-green-600">Labor Items</p>
          </div>
          <div className="p-4 rounded-lg bg-orange-50 text-center">
            <Wrench className="w-6 h-6 mx-auto text-orange-600 mb-2" />
            <p className="text-2xl font-bold text-orange-700">{analysis.equipment?.length || 0}</p>
            <p className="text-xs text-orange-600">Equipment</p>
          </div>
          <div className="p-4 rounded-lg bg-red-50 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto text-red-600 mb-2" />
            <p className="text-2xl font-bold text-red-700">{analysis.risks_and_issues?.length || 0}</p>
            <p className="text-xs text-red-600">Risks</p>
          </div>
        </div>
        
        {/* Cost Summary */}
        {analysis.costs_summary && (
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Cost Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(analysis.costs_summary).map(([key, value]) => (
                  <div key={key} className="flex justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-semibold">{value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Materials */}
        {analysis.materials?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                Materials ({analysis.materials.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analysis.materials.map((item, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{item.name || item.description}</p>
                        {item.specification && (
                          <p className="text-xs text-muted-foreground">{item.specification}</p>
                        )}
                      </div>
                      <div className="text-right">
                        {item.quantity && <p className="text-sm">{item.quantity}</p>}
                        {item.unit_cost && <p className="text-sm text-green-600">{item.unit_cost}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Risks */}
        {analysis.risks_and_issues?.length > 0 && (
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Risks & Issues ({analysis.risks_and_issues.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analysis.risks_and_issues.map((risk, idx) => (
                  <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-start gap-2">
                      <Badge variant="outline" className={`text-xs ${
                        risk.severity === 'high' ? 'bg-red-100 text-red-700' :
                        risk.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {risk.severity || 'medium'}
                      </Badge>
                      <div>
                        <p className="font-medium">{risk.description}</p>
                        {risk.recommendation && (
                          <p className="text-xs text-muted-foreground mt-1">
                            💡 {risk.recommendation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render chat interface
  const renderChat = () => (
    <div className="flex flex-col h-[500px]">
      {/* Chat Header */}
      <div className="flex items-center justify-between pb-3 border-b">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <span className="font-medium">AI Assistant</span>
          {chatMessages.length > 0 && (
            <Badge variant="secondary">{chatMessages.length} messages</Badge>
          )}
        </div>
        {chatMessages.length > 0 && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleExportChat}>
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleClearChat} className="text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Messages */}
      <ScrollArea className="flex-1 py-4">
        {chatMessages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">Ask questions about this document</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['Summarize costs', 'List materials', 'Any risks?', 'Key deadlines'].map((q) => (
                <Button 
                  key={q} 
                  variant="outline" 
                  size="sm"
                  onClick={() => setChatInput(q)}
                >
                  {q}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-xl ${
                  msg.role === 'user' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'assistant' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 h-6 text-xs opacity-60 hover:opacity-100"
                      onClick={() => {
                        navigator.clipboard.writeText(msg.content);
                        toast.success('Copied!');
                      }}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-muted p-3 rounded-xl flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </ScrollArea>
      
      {/* Input */}
      <div className="flex gap-2 pt-3 border-t">
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Ask about this document..."
          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChat()}
          disabled={chatLoading}
        />
        <Button onClick={handleChat} disabled={chatLoading || !chatInput.trim()}>
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="w-7 h-7 text-primary" />
            AI Document Analyzer
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
              Advanced
            </Badge>
          </h2>
          <p className="text-muted-foreground">
            Upload construction documents for AI-powered analysis
          </p>
        </div>
        
        {/* Upload Button */}
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => { loadDocuments(); loadStats(); toast.success('Refreshed!'); }}
            title="Refresh document list"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
              onChange={(e) => handleUpload(Array.from(e.target.files))}
              className="hidden"
              disabled={uploading}
            />
            <Button 
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              disabled={uploading}
              asChild
            >
              <span>
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload Documents</>
                )}
              </span>
            </Button>
          </label>
        </div>
      </div>
      
      {/* Upload Progress */}
      {uploading && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <div className="flex-1">
                <Progress value={uploadProgress} className="h-2" />
              </div>
              <span className="text-sm text-muted-foreground">{Math.round(uploadProgress)}%</span>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="library" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Library
          </TabsTrigger>
          <TabsTrigger value="quick" className="flex items-center gap-2">
            <Zap className="w-4 h-4" /> Quick Analyze
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Insights
          </TabsTrigger>
        </TabsList>
        
        {/* Library Tab */}
        <TabsContent value="library" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              value={stats.total} 
              label="Total" 
              icon={FileText} 
              color="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700" 
            />
            <StatCard 
              value={stats.analyzed} 
              label="Analyzed" 
              icon={CheckCircle} 
              color="bg-gradient-to-br from-green-50 to-green-100 text-green-700" 
            />
            <StatCard 
              value={stats.pending} 
              label="Pending" 
              icon={Clock} 
              color="bg-gradient-to-br from-yellow-50 to-yellow-100 text-yellow-700" 
            />
            <StatCard 
              value={stats.failed} 
              label="Failed" 
              icon={AlertCircle} 
              color="bg-gradient-to-br from-red-50 to-red-100 text-red-700" 
            />
          </div>
          
          {/* Search & Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="File Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="image">Images</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="word">Word</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Analyzed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Document List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">
                  {documents.length === 0 
                    ? 'No documents yet. Upload your first document to get started.'
                    : 'No documents match your filters.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredDocs.map(doc => (
                <DocumentCard key={doc.document_id} doc={doc} />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Quick Analysis Tab */}
        <TabsContent value="quick" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Quick Analysis
              </CardTitle>
              <CardDescription>
                Get instant AI analysis without saving the document
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File Input */}
              <div className="border-2 border-dashed rounded-xl p-8 text-center">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                  onChange={(e) => setQuickFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="quick-file"
                />
                <label htmlFor="quick-file" className="cursor-pointer">
                  {quickFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-8 h-8 text-primary" />
                      <span className="font-medium">{quickFile.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.preventDefault(); setQuickFile(null); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-muted-foreground">Click to select a file</p>
                    </>
                  )}
                </label>
              </div>
              
              {/* Question Input */}
              <Textarea
                placeholder="What would you like to know about this document? (Optional)"
                value={quickQuestion}
                onChange={(e) => setQuickQuestion(e.target.value)}
                rows={3}
              />
              
              {/* Analyze Button */}
              <Button 
                className="w-full"
                onClick={handleQuickAnalysis}
                disabled={!quickFile || quickAnalyzing}
              >
                {quickAnalyzing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Analyze Now</>
                )}
              </Button>
              
              {/* Results */}
              {quickResult && (
                <Card className="mt-4 bg-gradient-to-br from-purple-50 to-pink-50">
                  <CardHeader>
                    <CardTitle className="text-lg">Analysis Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap text-sm">{quickResult.analysis}</p>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Insights Tab */}
        <TabsContent value="insights" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Document Insights
              </CardTitle>
              <CardDescription>
                Analytics and trends from your document library
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl text-center">
                  <FileText className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-3xl font-bold text-blue-700">{stats.total}</p>
                  <p className="text-sm text-blue-600">Total Documents</p>
                </div>
                <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl text-center">
                  <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
                  <p className="text-3xl font-bold text-green-700">
                    {stats.total > 0 ? Math.round((stats.analyzed / stats.total) * 100) : 0}%
                  </p>
                  <p className="text-sm text-green-600">Analysis Rate</p>
                </div>
              </div>
              
              {stats.total === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto opacity-30 mb-4" />
                  <p>Upload documents to see insights</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Analysis Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedDoc?.filename}
            </DialogTitle>
            <DialogDescription>
              {selectedDoc?.analysis_status === 'completed' 
                ? 'AI-powered document analysis and chat'
                : 'Document details'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Dialog Tabs */}
          <Tabs value={dialogTab} onValueChange={setDialogTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-2 w-full max-w-xs">
              <TabsTrigger value="summary">Analysis</TabsTrigger>
              <TabsTrigger value="chat">Chat</TabsTrigger>
            </TabsList>
            
            <TabsContent value="summary" className="flex-1 overflow-auto mt-4">
              {selectedDoc?.analysis_result ? (
                renderAnalysis(selectedDoc.analysis_result)
              ) : (
                <div className="text-center py-12">
                  <Brain className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No analysis available</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="chat" className="flex-1 overflow-hidden mt-4">
              {renderChat()}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Close
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDelete(selectedDoc?.document_id)}
              disabled={deleting === selectedDoc?.document_id}
              data-testid="dialog-delete-btn"
            >
              {deleting === selectedDoc?.document_id ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Document</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentAnalyzer;
